import { useEffect, useRef } from "react";
import { useVisiblePoll } from "@/hooks/useVisiblePoll";
import { useOpportunitiesStore } from "./opportunitiesStore";

/** How often the board polls for which opportunities are actively being worked. */
const WORKING_POLL_INTERVAL_MS = 5000;

/**
 * Polls the opportunity liveness endpoint on an interval so the board's
 * "‹agent› is working" indicators appear and clear without a manual refresh.
 * Polling pauses while the browser tab is hidden and resumes with an immediate
 * fetch when it becomes visible again, so a backgrounded board costs nothing.
 * The working set is cleared on unmount.
 * @param onTick - Optional extra work to run on the same cadence (e.g. the live
 *   board delta sync). Invoked alongside the working fetch on every tick,
 *   including the immediate fetch on mount and on tab-visibility resume.
 */
export function useOpportunityLivenessPoll(onTick?: () => void): void {
  const fetchWorkingOpportunities = useOpportunitiesStore(
    (state) => state.fetchWorkingOpportunities,
  );
  const clearWorkingOpportunities = useOpportunitiesStore(
    (state) => state.clearWorkingOpportunities,
  );
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  useVisiblePoll(() => {
    fetchWorkingOpportunities();
    onTickRef.current?.();
  }, WORKING_POLL_INTERVAL_MS);

  // First fetch on mount (the shared poll only fires on its interval and on
  // tab resume); the working set is cleared on unmount.
  useEffect(() => {
    if (!document.hidden) {
      fetchWorkingOpportunities();
      onTickRef.current?.();
    }
    return () => {
      clearWorkingOpportunities();
    };
  }, [fetchWorkingOpportunities, clearWorkingOpportunities]);
}
